import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import BRAND from "../brand";
import { ArrowLeft } from "lucide-react";
import { useVoicePipeline } from "./simulator/useVoicePipeline";
import { SetupForm } from "./simulator/SetupForm";
import { VoiceSession } from "./simulator/VoiceSession";
import { SessionHeader } from "./simulator/SessionHeader";

export default function SimulatorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [scenarios, setScenarios] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState("");
  const [selectedPersona, setSelectedPersona] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("hinglish");
  const [micError, setMicError] = useState(null);

  const pipeline = useVoicePipeline();

  useEffect(() => { pipeline.setLanguageRef(selectedLanguage); }, [selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [s, p] = await Promise.all([axios.get(`${API}/scenarios`), axios.get(`${API}/personas`)]);
        setScenarios(s.data);
        setPersonas(p.data);
        if (s.data.length > 0) setSelectedScenario(s.data[0].id);
        if (p.data.length > 0) setSelectedPersona(p.data[0].id);
      } catch (e) {
        console.error("[Simulator] Failed to fetch config:", e);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchParams.get('scenario') && scenarios.length > 0) setSelectedScenario(searchParams.get('scenario'));
  }, [searchParams, scenarios]);

  const requestMicrophonePermission = async () => {
    try {
      setMicError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (e) {
      if (e.name === 'NotFoundError') setMicError("No microphone detected.");
      else if (e.name === 'NotAllowedError') setMicError("Microphone access denied.");
      else setMicError("Could not access microphone.");
      return false;
    }
  };

  const handleStartPractice = async () => {
    if (!selectedScenario) return;
    const hasPermission = await requestMicrophonePermission();
    if (!hasPermission) return;
    try {
      await pipeline.startConversation(selectedScenario, selectedPersona, selectedLanguage);
    } catch (e) {
      console.error("[Simulator] Start failed:", e);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-slate-500 hover:text-[#0B1F3B]"><ArrowLeft className="w-5 h-5" /></Button>
              </Link>
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-7 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              <div>
                <h1 className="text-lg font-bold text-[#0B1F3B]">Voice Practice</h1>
                <p className="text-xs text-slate-400">{BRAND.productName}</p>
              </div>
            </div>
            {pipeline.session && (
              <SessionHeader
                wsConnected={pipeline.wsConnected}
                selectedLanguage={selectedLanguage}
                sessionTimer={pipeline.sessionTimer}
                onEndConversation={() => pipeline.endConversation(navigate)}
              />
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!pipeline.session ? (
          <SetupForm
            scenarios={scenarios}
            personas={personas}
            selectedScenario={selectedScenario}
            setSelectedScenario={setSelectedScenario}
            selectedPersona={selectedPersona}
            setSelectedPersona={setSelectedPersona}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            micError={micError}
            requestMicPermission={requestMicrophonePermission}
            onStartPractice={handleStartPractice}
          />
        ) : (
          <VoiceSession
            session={pipeline.session}
            messages={pipeline.messages}
            voiceStatus={pipeline.voiceStatus}
            resistanceLevel={pipeline.resistanceLevel}
            turnCount={pipeline.turnCount}
            isMuted={pipeline.isMuted}
            currentTranscript={pipeline.currentTranscript}
            messagesEndRef={pipeline.messagesEndRef}
            selectedLanguage={selectedLanguage}
            onMicClick={pipeline.handleMicButtonClick}
            onToggleMute={pipeline.toggleMute}
          />
        )}
      </main>
    </div>
  );
}
